import { useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

/**
 * One way back, in one place, on every screen that has somewhere to go back to.
 *
 * Detail pages each grew their own back button in a slightly different spot,
 * which is three affordances to learn instead of one — and the screens reached
 * from a search result or a dashboard figure had none at all, leaving the browser
 * chrome as the only exit. This sits at the top of the content area, always the
 * same shape, always the same place.
 *
 * It prefers real history — going back should land where you actually came from,
 * which for a mitr opened off the sales leaderboard is the leaderboard, not the
 * mitr list. Only when there is no in-app history (a pasted link, a fresh tab)
 * does it fall back to the section above.
 */

interface Step {
  /** Matches the start of the path. */
  prefix: string;
  /** Where "up" goes when there is no history to use. */
  parent: string;
  /** What that place is called, in the words the nav uses. */
  label: string;
}

/*
 * Longest prefix wins, so `/admin/roles` is checked before `/admin`. Only routes
 * that are genuinely *inside* something appear here: a top-level destination has
 * nowhere to go back to, and a back button on one is a lie.
 */
const STEPS: Step[] = [
  { prefix: '/outlets/', parent: '/outlets', label: 'Company outlets' },
  { prefix: '/sub-dealers/', parent: '/sub-dealers', label: 'Mitrs' },
  { prefix: '/dealers/', parent: '/dealers', label: 'Dealers' },
  { prefix: '/vehicles/', parent: '/search', label: 'Vehicle search' },
  { prefix: '/financiers/', parent: '/financiers', label: 'Financiers' },
  { prefix: '/sync/', parent: '/sync', label: 'Sync' },
];

export function Backstep() {
  const location = useLocation();
  const navigate = useNavigate();

  const step = STEPS.filter((candidate) => location.pathname.startsWith(candidate.prefix)).sort(
    (a, b) => b.prefix.length - a.prefix.length,
  )[0];

  /*
   * Where we were last, tracked here rather than delegated to `navigate(-1)`.
   *
   * The browser's own history is not the app's: one step back from the first
   * screen after signing in is the login form, and one step back from a reload
   * is nowhere at all. Keeping the trail ourselves means "back" can only ever
   * land on a screen of this app, and we can name the destination before the
   * user commits to it.
   */
  const trail = useRef<string[]>([]);
  const here = location.pathname + location.search;

  useEffect(() => {
    const last = trail.current[trail.current.length - 1];
    if (last !== here) trail.current = [...trail.current.slice(-9), here];
  }, [here]);

  const previous = [...trail.current].reverse().find((entry) => entry !== here) ?? null;
  const goBack = useCallback(() => {
    if (previous) {
      // Drop everything from this screen back, so pressing back twice walks the
      // trail rather than bouncing between two screens.
      const at = trail.current.lastIndexOf(previous);
      trail.current = trail.current.slice(0, at);
      navigate(previous);
    } else if (step) {
      navigate(step.parent);
    }
  }, [navigate, previous, step]);

  if (!step) return null;

  return (
    <nav aria-label="Back" className="print-hide">
      <button
        type="button"
        onClick={goBack}
        className="tap-safe group -mx-2 inline-flex items-center gap-1.5 rounded-control px-2 py-1.5 text-micro font-medium text-muted transition-colors hover:bg-surface-2 hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        style={{ transitionDuration: 'var(--m-fast)' }}
      >
        <ArrowLeft
          className="h-3.5 w-3.5 shrink-0 transition-transform group-hover:-translate-x-0.5"
          style={{ transitionDuration: 'var(--m-fast)' }}
          aria-hidden
        />
        {/* Naming the destination is the difference between "back" and "back to
            what" — and on a deep link it is the only clue available. */}
        {previous ? 'Back' : `Back to ${step.label}`}
      </button>
    </nav>
  );
}
